import React, { useEffect, useState } from 'react';
import queryString from 'query-string';
import AuthModal from '../components/AuthModal';
import Footer from '../components/Footer';
import JoinUpdates from '../components/JoinUpdates';

const LandingPage = ({ session, logout }: { session: any; logout: () => void }) => {
    const queryParams = queryString.parse(window.location.search);
    const refIdParam = queryParams.refId;

    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
    const [authModalMode, setAuthModalMode] = useState<'login' | 'signup'>('login');
    const [isAuthDropdownOpen, setIsAuthDropdownOpen] = useState(false);
    const [modalType, setModalType] = React.useState<'success' | 'error' | 'already_registered'>('success');
	const [modalMessage, setModalMessage] = React.useState('');
    const [showModal, setShowModal] = React.useState(false);
	const [isLoading, setIsLoading] = React.useState(false);
    const [refId, setRefId] = useState<string>('');

    useEffect(() => {
        document.documentElement.style.scrollBehavior = 'smooth';
        return () => {
            document.documentElement.style.scrollBehavior = 'auto';
        };
    }, []);

    useEffect(() => {
        if (refIdParam) {
            setRefId(refIdParam as string);
            // Optionally dispatch refId to backend here if needed
        }
    }, [refIdParam]);
    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();

		const formData = {
			firstName: event.currentTarget.firstName.value,
			lastName: event.currentTarget.lastName.value,
			email: event.currentTarget.email.value,
			gardenSize: event.currentTarget.gardenSize.value,
		};

		console.log('Form Data:', formData); // Log the form data

		// Start loading
		setIsLoading(true);

		try {
			const response = await fetch(`${process.env.REACT_APP_API_URL_JOIN_UPDATE}/api/waitlist`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify(formData),
			});

			const data = await response.json();

			if (response.ok) {
				// Check if user already exists using the new backend response format
				if (data.user && data.user.id) {
					if (data.isExistingUser) {
						// User already registered
						setModalType('already_registered');
						setModalMessage('You\'re already on our waitlist! Check your email for your referral details.');
					} else {
						// New user successfully registered
						setModalType('success');
						setModalMessage('Thank you for joining our waitlist! We\'ve sent your referral code and registration details to your email.');
						
						// Handle referral if refId exists
						if (refId !== null && refId !== '') {
							console.log({ refId });
							try {
								await fetch(`${process.env.REACT_APP_API_URL}/api/referral`, {
									method: 'POST',
									headers: {
										'Content-Type': 'application/json',
									},
									body: JSON.stringify({
										refId: refId,
									}),
								});
							} catch (refError) {
								console.error('Referral error:', refError);
								// Don't fail the whole process for referral errors
							}
						}

						// Clear form only for new registrations
						if (event.currentTarget) {
							event.currentTarget.reset();
						}
					}
				} else {
					// Unexpected response format
					setModalType('error');
					setModalMessage('Something went wrong. Please try again.');
				}
				
				setShowModal(true);
			} else {
				// Handle different error types
				if (response.status === 400) {
					setModalType('error');
					setModalMessage(data.error || 'Please check your information and try again.');
				} else if (response.status === 500) {
					setModalType('error');
					setModalMessage('Server error. Please try again later.');
				} else {
					setModalType('error');
					setModalMessage('Something went wrong. Please try again.');
				}
				setShowModal(true);
			}
		} catch (error) {
			console.error('Error:', error);
			setModalType('error');
			setModalMessage('Network error. Please check your connection and try again.');
			setShowModal(true);
		} finally {
			// Stop loading
			setIsLoading(false);
		}
	};


    const handleProtectedAction = (e: React.MouseEvent, targetId: string) => {
        e.preventDefault();
        if (!session) {
            setAuthModalMode('login');
            setIsAuthModalOpen(true);
        } else {
            window.location.hash = '#/dashboard';
        }
    };

    const openAuth = (mode: 'login' | 'signup') => {
        setAuthModalMode(mode);
        setIsAuthModalOpen(true);
        setMobileMenuOpen(false);
    };

    const handleLogout = logout;

    return (
        <div className="min-h-screen bg-gradient-to-b from-white to-green-50">
            {/* Nav */}
            <nav className="fixed w-full bg-white/80 backdrop-blur-sm z-50">
                <div className="container mx-auto px-4 py-4 flex justify-between items-center">
                    <div
                        className="text-3xl md:text-4xl font-bold text-green-600 whitespace-nowrap cursor-pointer"
                        style={{ fontFamily: 'Comic Sans MS, Comic Sans, cursive' }}
                        onClick={() => window.scrollTo(0, 0)}
                    >
                        MitiFresh🥭
                    </div>

                    {/* Mobile menu button */}
                    <button
                        onClick={() => {
                            setMobileMenuOpen(!mobileMenuOpen);
                            setIsAuthDropdownOpen(false);
                        }}
                        className="md:hidden p-2 rounded-md text-gray-600 hover:text-green-600 hover:bg-gray-100"
                    >
                        <img
                            src={mobileMenuOpen ? "/icons/close.svg" : "/icons/menu.svg"}
                            alt={mobileMenuOpen ? "Close menu" : "Open menu"}
                            className="h-6 w-6 brightness-0 saturate-100 hue-rotate-180"
                        />
                    </button>

                    {/* Desktop menu */}
                    <div className="hidden md:flex gap-8">
                        <a href="#how-it-works" className="text-gray-600 hover:text-green-600 transition-colors">How it Works</a>
                        <a href="#benefits" className="text-gray-600 hover:text-green-600 transition-colors">Benefits</a>
                        <a href="#waitlist" className="text-gray-600 hover:text-green-600 transition-colors">Join Waitlist</a>
                    </div>

                    <div className="hidden md:flex items-center gap-3">
                        {!session ? (
                            <>
                                <button 
                                    onClick={() => setIsAuthDropdownOpen(!isAuthDropdownOpen)} 
                                    className="bg-black text-white px-6 py-2 rounded-full hover:bg-gray-800 transition-colors font-medium"
                                >
                                    Get Started
                                </button>

                                {isAuthDropdownOpen && (
                                    <>
                                        <div className="fixed inset-0 z-40" onClick={() => setIsAuthDropdownOpen(false)} />
                                        <div className="absolute right-0 top-full mt-2 w-48 bg-white border border-gray-100 rounded-2xl p-4 shadow-xl z-50 flex flex-col gap-2 animate-in fade-in slide-in-from-top-1 duration-200">
                                            <button 
                                                onClick={() => { openAuth('login'); setIsAuthDropdownOpen(false); }} 
                                                className="w-full py-2 bg-white text-black border border-gray-200 rounded-full text-sm font-medium hover:bg-gray-50 transition-colors"
                                            >
                                                Log in
                                            </button>
                                            <button 
                                                onClick={() => { openAuth('signup'); setIsAuthDropdownOpen(false); }} 
                                                className="w-full py-2 bg-green-600 text-white rounded-full text-sm font-medium hover:bg-green-700 transition-colors"
                                            >
                                                Sign up
                                            </button>
                                        </div>
                                    </>
                                )}
                            </>
                        ) : (
                            <>
                                <button onClick={(e) => handleProtectedAction(e, 'waitlist')} className="bg-black text-white px-6 py-2 rounded-full hover:bg-gray-800 transition-colors font-medium">
                                    Get Started
                                </button>
                                <button onClick={handleLogout} className="bg-white text-black border border-black px-6 py-2 rounded-full hover:bg-gray-100 transition-colors font-medium">
                                    Log out
                                </button> 
                            </>
                        )}
                    </div>
                </div>

                {/* Mobile menu */}
                <div className={`md:hidden ${mobileMenuOpen ? 'block' : 'hidden'} bg-white border-t`}>
                    <div className="px-4 py-2 space-y-3">
                        <a href="#how-it-works" className="block text-gray-600 hover:text-green-600 transition-colors py-2">How it Works</a>
                        <a href="#benefits" className="block text-gray-600 hover:text-green-600 transition-colors py-2">Benefits</a>
                        <a href="#waitlist" className="block text-gray-600 hover:text-green-600 transition-colors py-2">Join Waitlist</a>

                        {!session ? (
                            <div className="flex flex-col gap-2 pb-2 w-full">
                                {!isAuthDropdownOpen ? (
                                    <button 
                                        onClick={() => setIsAuthDropdownOpen(true)} 
                                        className="w-full bg-black text-white px-6 py-2 rounded-full hover:bg-gray-800 transition-colors font-medium text-center"
                                    >
                                        Get Started
                                    </button>
                                ) : (
                                    <div className="flex flex-col gap-2 w-full animate-in fade-in slide-in-from-top-1 duration-150">
                                        <button 
                                            onClick={() => { openAuth('login'); setIsAuthDropdownOpen(false); }} 
                                            className="w-full py-2 bg-white text-black border border-gray-200 rounded-full text-sm font-medium hover:bg-gray-50 transition-colors text-center shadow-sm"
                                        >
                                            Log in
                                        </button>
                                        <button 
                                            onClick={() => { openAuth('signup'); setIsAuthDropdownOpen(false); }} 
                                            className="w-full py-2 bg-green-600 text-white rounded-full text-sm font-medium hover:bg-green-700 transition-colors text-center shadow-sm"
                                        >
                                            Sign up
                                        </button>
                                    </div>
                                )}
                            </div>
                        ) : null}
                    </div>
                </div>
            </nav>

            {/* Hero Section */}
            <header className="pt-32 pb-20 relative overflow-hidden">
                <div className="absolute inset-0 opacity-15">
                    <img src="/garden-hero.jpg" alt="Beautiful Garden" className="w-full h-full object-cover" />
                </div>
                <div className="container mx-auto px-4 relative z-10">
                    <div className="max-w-3xl mx-auto text-center">
                        <div className="inline-block mb-6 px-4 py-1 bg-green-100 rounded-full text-green-700 font-medium text-sm">
                            🌱 Transforming Backyards into Micro-Orchards
                        </div>
                        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                            Turn Your Garden Into a<span className="text-green-600"> Profitable Ecosystem</span>
                        </h1>
                        <p className="text-lg md:text-xl text-gray-600 mb-10 leading-relaxed">
                            Connects home gardeners with their neighbors, enabling them to sell their fresh, homegrown produce locally.
                        </p>
                        <div className="flex gap-4 justify-center mb-16">
                            <button
                                onClick={(e) => handleProtectedAction(e, 'waitlist')}
                                className="bg-green-600 text-white px-8 py-3 rounded-full font-medium hover:bg-green-700 shadow-lg shadow-green-200 transition-all"
                            >
                                Start Earning
                            </button>
                            <a
                                href="https://apps.apple.com/us/app/mitifresh/id6742926258"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="bg-black text-white px-8 py-3 rounded-full font-medium hover:bg-gray-800 shadow-lg flex items-center gap-2"
                            >
                                <img src="/icons/apple.svg" alt="Apple" className="w-5 h-5 brightness-0 invert" />
                                Download iOS App
                            </a>
                        </div>
                    </div>
                </div>
            </header>

            {/* Demo Video Section */}
            <section className="py-16 bg-white">
                <div className="container mx-auto px-4">
                    <div className="max-w-4xl mx-auto">
                        <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-8">See MitiFresh in Action</h2>
                        <div className="aspect-w-16 aspect-h-9 rounded-2xl overflow-hidden shadow-2xl">
                            <video
                                className="w-full h-full object-cover"
                                controls
                                poster="https://xtclgfcwyrrslofmfmrg.supabase.co/storage/v1/object/public/demo/thumbnail.jpg"
                                preload="metadata"
                                playsInline
                            >
                                <source src="https://xtclgfcwyrrslofmfmrg.supabase.co/storage/v1/object/public/demo/demo.mp4" type="video/mp4" />
                                Your browser does not support the video tag.
                            </video>
                        </div>
                        <p className="text-center text-gray-600 mt-6">
                            Watch how MitiFresh transforms backyard gardens into thriving micro-orchards
                        </p>
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section id="how-it-works" className="py-20 scroll-mt-20 bg-gradient-to-b from-green-50 to-green-100">
                <div className="container mx-auto px-4">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl font-bold mb-4">How MitiFresh Works</h2>
                        <p className="text-gray-600 max-w-2xl mx-auto">
                            Three simple steps to transform your garden into a sustainable revenue stream
                        </p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-12">
                        <FeatureCard emoji="🌳" title="Register Your Trees" description="Quick scan of your garden to assess yield potential and market value" number={1} />
                        <FeatureCard emoji="👩‍🌾" title="Locate your neighbors" description="Connect with your local neighbors who are also fruitarians" number={2} />
                        <FeatureCard emoji="💰" title="Earn Passively" description="Sell your fresh produce to earn passive income" number={3} />
                    </div>
                </div>
            </section>

            {/* Image Divider Section */}
            <section className="relative py-20 bg-black text-white overflow-hidden">
                <div className="absolute inset-0 opacity-20">
                    <img src="/fruit-pattern.jpg" alt="Fruit Pattern" className="w-full h-full object-cover" />
                </div>
                <div className="container mx-auto px-4 relative z-10">
                    <div className="text-center mb-12">
                        <h2 className="text-4xl font-bold mb-4">Join Our Community</h2>
                        <p className="text-xl text-gray-300">Connect with your neighbors and earn together</p>
                    </div>
                    {/* removed the statCard */}
                </div>
            </section>

            {/* Benefits Grid */}
            <section id="benefits" className="py-20 scroll-mt-20 bg-gradient-to-b from-green-50 to-green-100">
                <div className="container mx-auto px-4">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl font-bold mb-4">Why Choose MitiFresh</h2>
                        <p className="text-gray-600 max-w-2xl mx-auto">Join thousands of garden owners already earning passive income</p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                        <BenefitCard title="Passive Income" description="Earn monthly revenue from your existing fruit trees with zero effort" />
                        <BenefitCard title="Local Impact" description="Support your community with fresh, locally-grown produce" />
                        <BenefitCard title="Zero Risk" description="No upfront costs - we only earn when you earn" />
                    </div>
                </div>
            </section>

            {/* <JoinUpdates /> */}
            {/* Waitlist Section */}
			<section
				id="waitlist"
				className="py-20 bg-green-600 text-white relative overflow-hidden scroll-mt-20"
			>
				<div className="absolute inset-0 opacity-20">
					<img
						src="/fruit-pattern-light.jpg"
						alt="Fruit Pattern"
						className="w-full h-full object-cover"
					/>
				</div>
				<div className="container mx-auto px-4 relative z-10">
					<div className="max-w-3xl mx-auto">
						<div className="text-center">
							<div className="inline-block mb-6 px-4 py-1 bg-green-500 rounded-full text-white text-sm">🚀 Early Access</div>
							<h2 className="text-4xl font-bold mb-6">Join the Waitlist</h2>
							<p className="text-xl mb-12 text-green-100">
								Be among the first to transform your garden into a profitable ecosystem. Early adopters get lifetime premium
								benefits.
							</p>
						</div>

						<div className="bg-white/60 backdrop-blur-md rounded-2xl p-8 shadow-xl">
							<form
								onSubmit={handleSubmit}
								className="space-y-6"
							>
								<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
									<div>
										<label className="block text-gray-700 text-sm font-medium mb-2">First Name</label>
										<input
											type="text"
											name="firstName"
											className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-black"
											placeholder="John"
										/>
									</div>
									<div>
										<label className="block text-gray-700 text-sm font-medium mb-2">Last Name</label>
										<input
											type="text"
											name="lastName"
											className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-black"
											placeholder="Doe"
										/>
									</div>
								</div>

								<div>
									<label className="block text-gray-700 text-sm font-medium mb-2">
										Email Address <span className="text-red-500">*</span>
									</label>
									<input
										type="email"
										name="email"
										required
										defaultValue={session?.user?.email || ''}
    									readOnly={!!session}
										// className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-black"
										className={`w-full px-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-black ${
											session
												? 'bg-gray-100 text-gray-500 cursor-not-allowed'
												: ''
										}`}
										placeholder="email@address.com"
									/>
								</div>

								<div>
									<label className="block text-gray-700 text-sm font-medium mb-2">Tell us about your garden</label>
									<select
										name="gardenSize"
										className="w-full px-4 py-2 rounded-lg border border-gray-200 text-black"
									>
										<option value="">Select number of fruit trees</option>
										<option value="0">I am interested in buying</option>
										<option value="1-2">1-2 trees</option>
										<option value="3-5">3-5 trees</option>
										<option value="6-10">6-10 trees</option>
										<option value="10+">More than 10 trees</option>
									</select>
								</div>

								<div className="flex items-start">
									<input
										type="checkbox"
										className="mt-1 h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
									/>
									<label className="ml-2 block text-sm text-gray-600">
										I agree to receive updates about MitiFresh. You can unsubscribe at any time.
									</label>
								</div>

								<button
									type="submit"
									disabled={isLoading}
									className={`w-full px-8 py-3 rounded-lg font-medium transition-colors flex items-center justify-center ${
										isLoading
											? 'bg-gray-400 cursor-not-allowed'
											: 'bg-black text-white hover:bg-gray-900'
									}`}
								>
									{isLoading ? (
										<>
													<img
														src="/icons/spinner.svg"
														alt="Loading"
														className="animate-spin -ml-1 mr-3 h-5 w-5 brightness-0 invert"
													/>
											Joining...
										</>
									) : (
										'Join the Waitlist'
									)}
								</button>
							</form>


							<div className="mt-6 text-center text-sm text-gray-500 flex items-center justify-center gap-4">
								<div className="flex items-center">🔒 SSL Secured</div>
								<div className="flex items-center">⚡️ 392 people joined</div>
							</div>
						</div>

						<div className="mt-8 text-center text-sm text-green-100">
							Limited spots available. Early adopters get lifetime premium benefits.
						</div>
					</div>
				</div>
			</section>
            <Footer />
            {showModal && (
				<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
					<div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl">
						<div className="text-center">
							{/* Success Modal */}
							{modalType === 'success' && (
								<>
									<div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
										<img src="/icons/check.svg" alt="Check" className="w-8 h-8 brightness-0 saturate-100 hue-rotate-120" />
									</div>
									<h3 className="text-2xl font-bold text-gray-900 mb-4">Welcome to MitiFresh! 🌱</h3>
									<p className="text-gray-600 mb-6">
										{modalMessage}
									</p>
									<div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
										<div className="flex items-center justify-center mb-2">
											<img src="/icons/email.svg" alt="Email" className="w-5 h-5 brightness-0 saturate-100 hue-rotate-120 mr-2" />
											<span className="text-sm font-medium text-green-800">Check Your Email</span>
										</div>
										<p className="text-xs text-green-700">Please check your inbox (and spam folder) for your referral link.</p>
									</div>
									<button
										onClick={() => setShowModal(false)}
										className="w-full bg-green-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-green-700 transition-colors"
									>
										Got it!
									</button>
								</>
							)}

							{/* Already Registered Modal */}
							{modalType === 'already_registered' && (
								<>
									<div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
										<img src="/icons/check.svg" alt="Check" className="w-8 h-8 brightness-0 saturate-100 hue-rotate-240" />
									</div>
									<h3 className="text-2xl font-bold text-gray-900 mb-4">Already Registered! 📧</h3>
									<p className="text-gray-600 mb-6">
										{modalMessage}
									</p>
									<div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
										<div className="flex items-center justify-center mb-2">
											<img src="/icons/email.svg" alt="Email" className="w-5 h-5 brightness-0 saturate-100 hue-rotate-240 mr-2" />
											<span className="text-sm font-medium text-blue-800">Check Your Email</span>
										</div>
										<p className="text-xs text-blue-700">Your referral details should be in your inbox or spam folder.</p>
									</div>
									<button
										onClick={() => setShowModal(false)}
										className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors"
									>
										Understood!
									</button>
								</>
							)}

							{/* Error Modal */}
							{modalType === 'error' && (
								<>
									<div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
										<img src="/icons/close.svg" alt="Error" className="w-8 h-8 brightness-0 saturate-100 hue-rotate-0" />
									</div>
									<h3 className="text-2xl font-bold text-gray-900 mb-4">Oops! Something went wrong 😔</h3>
									<p className="text-gray-600 mb-6">
										{modalMessage}
									</p>
									<div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
										<div className="flex items-center justify-center mb-2">
											<img src="/icons/close.svg" alt="Error" className="w-5 h-5 brightness-0 saturate-100 hue-rotate-0 mr-2" />
											<span className="text-sm font-medium text-red-800">Please Try Again</span>
										</div>
										<p className="text-xs text-red-700">If the problem persists, please contact our support team.</p>
									</div>
									<div className="flex gap-3">
										<button
											onClick={() => setShowModal(false)}
											className="flex-1 bg-gray-500 text-white px-6 py-3 rounded-lg font-medium hover:bg-gray-600 transition-colors"
										>
											Close
										</button>
										<button
											onClick={() => {
												setShowModal(false);
												// Scroll to form
												document.getElementById('waitlist')?.scrollIntoView({ behavior: 'smooth' });
							}}
											className="flex-1 bg-red-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-red-700 transition-colors"
										>
											Try Again
										</button>
									</div>
								</>
							)}
						</div>
					</div>
				</div>
			)}
            <AuthModal isOpen={isAuthModalOpen} onClose={() => setIsAuthModalOpen(false)} initialMode={authModalMode} />
        </div>
    );
};

const FeatureCard = ({ emoji, title, description, number }: { emoji: string; title: string; description: string; number: number }) => (
    <div className="p-6 rounded-2xl bg-white shadow-xl shadow-green-50">
        <div className="text-4xl mb-4">{emoji}</div>
        <div className="text-xs font-medium text-green-600 mb-2">STEP {number}</div>
        <h3 className="text-lg font-bold">{title}</h3>
        <p>{description}</p>
    </div>
);

const BenefitCard = ({ title, description }: { title: string; description: string }) => (
    <div className="bg-white p-6 rounded-2xl shadow-lg shadow-green-50 hover:shadow-xl transition-shadow">
        <h3 className="text-lg font-bold mb-3 text-gray-900">{title}</h3>
        <p className="text-gray-600 text-sm">{description}</p>
    </div>
);

const StatCard = ({ number, label }: { number: string | number; label: string }) => (
    <div className="text-center">
        <div className="text-4xl font-bold mb-2">{number}</div>
        <div className="text-gray-400 text-sm">{label}</div>
    </div>
);

export default LandingPage;